import { FC, FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

import { ArrowBack, CloudDownload } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, Grid, Link, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material'

import { formatSeconds } from '../../../features/ftue-videos/components/FtueVideoPlayer/formatSeconds'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useAttachFtueVideoMutation, useGetFtueGameVersionsQuery, useGetFtueVideosByGameQuery } from '../../api/ftue-videos'
import { useGetInternalGameQuery } from '../../api/internal-games'
import { useLazyGetVimeoOEmbedQuery } from '../../api/vimeo'
import { FtueGameHeader } from '../../features/ftue-videos/components/FtueGameHeader/FtueGameHeader'
import {
  findVideoWithVimeoId,
  formatReleaseDate,
  ftueVideoFieldLabelKeys,
  getDefaultMarketIso,
  getFtueMarketOptions,
  getUnstoredFtueVideoFields,
  toFtueVideoAttachBody,
} from '../../features/ftue-videos/helpers/ftueVideoHelpers'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { parseVimeoId, toVimeoThumbnails } from '../../helpers/vimeo'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { ftueVideoEditPath, ftueVideosGamePath, ftueVideosPath } from './FtueVideosPage'

const errorStatus = (error: unknown) => (typeof error === 'object' && error !== null && 'status' in error ? (error as { status: unknown }).status : undefined)

export const FtueVideoAttachPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-ftue-video-attach'))
  const { t } = useTranslation()
  const { notify, notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const gameId = searchParams.get('gameId') ?? ''
  const gameQuery = useGetInternalGameQuery(gameId, { skip: !gameId })
  const gameVideosQuery = useGetFtueVideosByGameQuery(gameId, { skip: !gameId })
  const [loadOEmbed, oembedResult] = useLazyGetVimeoOEmbedQuery()
  const [attachVideo, { isLoading: isAttaching }] = useAttachFtueVideoMutation()
  const [input, setInput] = useState('')
  const [touched, setTouched] = useState(false)
  const [nameEdit, setNameEdit] = useState<{ vimeoId: string; name: string }>()
  const [selectedMarketIso, setSelectedMarketIso] = useState('')
  const [selectedVersion, setSelectedVersion] = useState('')

  const game = gameQuery.data
  const marketOptions = useMemo(() => getFtueMarketOptions(game?.markets), [game?.markets])
  const marketIso = selectedMarketIso || getDefaultMarketIso(marketOptions)
  const versionsQuery = useGetFtueGameVersionsQuery({ appId: game?.appId ?? 0, marketIso }, { skip: !game?.appId || !marketIso })
  const versions = versionsQuery.data ?? []
  const version = versions.find((option) => option.version === selectedVersion)

  const vimeoId = parseVimeoId(input)
  const oembed = oembedResult.isSuccess && oembedResult.originalArgs === vimeoId ? oembedResult.data : undefined
  const name = oembed && nameEdit?.vimeoId === oembed.vimeoId ? nameEdit.name : oembed?.title ?? ''
  const existing = vimeoId ? findVideoWithVimeoId(gameVideosQuery.data, vimeoId) : undefined
  const attachBody = oembed && version && name.trim() ? toFtueVideoAttachBody({ gameId, oembed, name, marketIso, version }) : undefined

  const handleLoad = (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (vimeoId) loadOEmbed(vimeoId)
  }

  const oembedErrorKey = () => {
    const status = errorStatus(oembedResult.error)
    if (status === 404) return 'internal-analysts:ftue_videos_vimeo_not_found'
    if (status === 403) return 'internal-analysts:ftue_videos_vimeo_restricted'
    return 'internal-analysts:ftue_videos_vimeo_failed'
  }

  const handleAttach = async () => {
    if (!attachBody) return
    const listedBefore = gameVideosQuery.data
    const videoName = attachBody.names.en
    try {
      const stored = await attachVideo(attachBody).unwrap()
      const unstored = getUnstoredFtueVideoFields(attachBody, stored)
      if (unstored.length) {
        const fields = unstored.map((field) => t(ftueVideoFieldLabelKeys[field])).join(', ')
        notifyError(t('internal-analysts:ftue_videos_not_stored', { fields }))
      } else {
        notifySuccess(t('internal-analysts:ftue_videos_attached_named', { name: videoName }))
      }
      navigate(ftueVideoEditPath(stored.id), { replace: true })
    } catch (error) {
      const listed =
        errorStatus(error) === 409
          ? undefined
          : await gameVideosQuery
              .refetch()
              .unwrap()
              .catch(() => undefined)
      const attached = findVideoWithVimeoId(listed, attachBody.vimeoId)
      if (attached && !findVideoWithVimeoId(listedBefore, attachBody.vimeoId)) {
        notify(t('internal-analysts:ftue_videos_attached_unconfirmed', { name: videoName }), 'warning')
        navigate(ftueVideoEditPath(attached.id), { replace: true })
        return
      }
      const reason = getValidationErrorMessage(error)
      notifyError(reason ? t('internal-analysts:ftue_videos_attach_failed_reason', { reason }) : t('internal-analysts:ftue_videos_attach_failed'))
    }
  }

  const backPath = gameId ? ftueVideosGamePath(gameId) : ftueVideosPath
  const previewThumbnail = oembed ? toVimeoThumbnails(oembed.thumbnailUrl, { keepQuery: true })[3] : undefined

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(backPath)}>
            {t('internal-common:back')}
          </Button>
        </Stack>
        <Button variant="contained" onClick={handleAttach} disabled={!attachBody || !!existing || isAttaching || !gameVideosQuery.isSuccess}>
          {t('internal-analysts:ftue_videos_attach')}
        </Button>
      </Stack>

      {!gameId ? (
        <Alert severity="info">
          {t('internal-analysts:ftue_videos_attach_needs_game')}{' '}
          <Link component={RouterLink} to={ftueVideosPath}>
            {t('internal-analysts:ftue_videos_all')}
          </Link>
        </Alert>
      ) : gameQuery.isError ? (
        <Typography color="error.main">{t('internal-analysts:ftue_videos_game_load_failed')}</Typography>
      ) : (
        <Stack spacing={2}>
          <FtueGameHeader game={game} isLoading={gameQuery.isLoading} />

          <Box component="form" onSubmit={handleLoad} noValidate>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                size="small"
                label={t('internal-analysts:ftue_videos_vimeo_input')}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onBlur={() => setTouched(true)}
                error={touched && !!input && !vimeoId}
                helperText={
                  touched && !!input && !vimeoId ? t('internal-analysts:ftue_videos_vimeo_input_invalid') : t('internal-analysts:ftue_videos_vimeo_input_help')
                }
                sx={{ minWidth: 420 }}
              />
              <Button type="submit" variant="outlined" startIcon={<CloudDownload />} disabled={!vimeoId || oembedResult.isFetching}>
                {t('internal-analysts:ftue_videos_vimeo_load')}
              </Button>
            </Stack>
          </Box>

          {existing && (
            <Alert severity="warning">
              {t('internal-analysts:ftue_videos_vimeo_in_use', { name: existing.names?.en || '---' })}{' '}
              <Link component={RouterLink} to={ftueVideoEditPath(existing.id)}>
                {t('internal-analysts:ftue_videos_open_existing')}
              </Link>
            </Alert>
          )}

          {oembedResult.isFetching ? (
            <Skeleton variant="rectangular" height={320} />
          ) : oembedResult.isError && oembedResult.originalArgs === vimeoId ? (
            <Alert severity="error">{t(oembedErrorKey(), { id: oembedResult.originalArgs })}</Alert>
          ) : (
            oembed && (
              <Card>
                <CardContent>
                  <Grid container spacing={3} wrap="wrap">
                    <Grid item xs={12} md={7}>
                      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9' }}>
                        <Box
                          component="iframe"
                          title={oembed.title}
                          src={`https://player.vimeo.com/video/${oembed.vimeoId}`}
                          allow="autoplay; fullscreen; picture-in-picture"
                          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                          {t('internal-analysts:ftue_videos_vimeo_details', { id: oembed.vimeoId, duration: formatSeconds(oembed.durationSeconds) })}
                        </Typography>
                        <TextField
                          size="small"
                          label={t('internal-analysts:ftue_videos_name')}
                          value={name}
                          onChange={(event) => setNameEdit({ vimeoId: oembed.vimeoId, name: event.target.value })}
                          error={!name.trim()}
                          helperText={!name.trim() ? t('internal-analysts:ftue_videos_name_required') : t('internal-analysts:ftue_videos_name_help')}
                          fullWidth
                        />
                        <TextField
                          select
                          size="small"
                          label={t('internal-analysts:ftue_videos_market')}
                          value={marketOptions.some((option) => option.iso === marketIso) ? marketIso : ''}
                          onChange={(event) => {
                            setSelectedMarketIso(event.target.value)
                            setSelectedVersion('')
                          }}
                          fullWidth
                        >
                          {marketOptions.map((option) => (
                            <MenuItem key={option.iso} value={option.iso}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          size="small"
                          label={t('internal-analysts:ftue_videos_version')}
                          value={version ? version.version : ''}
                          onChange={(event) => setSelectedVersion(event.target.value)}
                          disabled={!versions.length}
                          error={!version && !versionsQuery.isFetching}
                          helperText={
                            versionsQuery.isFetching
                              ? t('internal-analysts:ftue_videos_versions_loading')
                              : versionsQuery.isError
                              ? t('internal-analysts:ftue_videos_versions_failed')
                              : !versions.length
                              ? t('internal-analysts:ftue_videos_versions_none')
                              : !version
                              ? t('internal-analysts:ftue_videos_version_required')
                              : t('internal-analysts:ftue_videos_release_date_value', { date: formatReleaseDate(version.versionReleaseDate) })
                          }
                          fullWidth
                        >
                          {versions.map((option) => (
                            <MenuItem key={option.version} value={option.version}>
                              {t('internal-analysts:ftue_videos_version_option', {
                                version: option.version,
                                date: formatReleaseDate(option.versionReleaseDate),
                              })}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Typography variant="subtitle2">{t('internal-analysts:ftue_videos_thumbnail')}</Typography>
                        {previewThumbnail && (
                          <Box
                            component="img"
                            src={previewThumbnail.link}
                            alt={t('internal-analysts:ftue_videos_thumbnail')}
                            sx={{ display: 'block', width: '100%' }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {t('internal-analysts:ftue_videos_thumbnail_current_note')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('internal-analysts:ftue_videos_attach_note')}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )
          )}
        </Stack>
      )}
    </Box>
  )
}
